import { useMemo } from 'react';

import { Grid, type BoxProps } from '@frontend/components';

import { SHADOW_REGION_TO_GRID_AREA_MAP } from './const';

export const FALLBACK_GRID_SIZE_VALUE = 'auto';

export const getGridSizeValueFromStickyContentBoxValue = (
  value: string | number,
): string => {
  if (typeof value === 'number' && isFinite(value) && value >= 0) {
    return `${value}px`;
  }

  if (typeof value === 'string' && value) {
    return value;
  }

  return FALLBACK_GRID_SIZE_VALUE;
};

export type ShadowContainerProps = BoxProps & {
  overflowBox: { top: number; right: number; bottom: number; left: number };
  stickyContentBox: {
    top: string | number;
    right: string | number;
    bottom: string | number;
    left: string | number;
  };
  depth?: number;
};

export const ShadowContainer = ({
  overflowBox,
  stickyContentBox,
  depth = 8,
  sx,
  children,
  ...restProps
}: ShadowContainerProps) => {
  const gridSizeValues = useMemo(
    () => ({
      top: getGridSizeValueFromStickyContentBoxValue(stickyContentBox.top),
      right: getGridSizeValueFromStickyContentBoxValue(stickyContentBox.right),
      bottom: getGridSizeValueFromStickyContentBoxValue(
        stickyContentBox.bottom,
      ),
      left: getGridSizeValueFromStickyContentBoxValue(stickyContentBox.left),
    }),
    [stickyContentBox],
  );

  return (
    <Grid
      position="absolute"
      pointerEvents="none"
      aria-hidden="true"
      zIndex={10}
      inset={0}
      style={{
        ['--overflow-top' as string]: overflowBox.top + 'px',
        ['--overflow-right' as string]: overflowBox.right + 'px',
        ['--overflow-bottom' as string]: overflowBox.bottom + 'px',
        ['--overflow-left' as string]: overflowBox.left + 'px',
        ['--depth-unit' as string]: depth + 'px',
        ['--spread' as string]: 'calc((-1 * var(--depth)) - 2px)',
        ['--color' as string]: 'rgba(0,0,0,0.3)',
      }}
      gridTemplate={`
        "${SHADOW_REGION_TO_GRID_AREA_MAP.topLeft} ${SHADOW_REGION_TO_GRID_AREA_MAP.topMiddle} ${SHADOW_REGION_TO_GRID_AREA_MAP.topRight}" ${gridSizeValues.top}
        "${SHADOW_REGION_TO_GRID_AREA_MAP.middleLeft} ${SHADOW_REGION_TO_GRID_AREA_MAP.inside} ${SHADOW_REGION_TO_GRID_AREA_MAP.middleRight}"
        "${SHADOW_REGION_TO_GRID_AREA_MAP.bottomLeft} ${SHADOW_REGION_TO_GRID_AREA_MAP.bottomMiddle} ${SHADOW_REGION_TO_GRID_AREA_MAP.bottomRight}" ${gridSizeValues.bottom}
        / ${gridSizeValues.left} 1fr ${gridSizeValues.right}
      `}
      sx={{
        '*': {
          position: 'absolute',
        },
        ...sx,
      }}
      {...restProps}
    >
      {children}
    </Grid>
  );
};
